import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Activity, Plus, ArrowRight, Sparkles } from "lucide-react";
import { toast } from 'sonner';
import { format } from 'date-fns';
import { UniversalSymptomTracker } from '@/components/mental/UniversalSymptomTracker';
import { useUniversalTracking } from '@/hooks/useUniversalTracking';

interface ActivityLog {
  id: string;
  date: string;
  activity: string;
  pleasure: number;
  mastery: number;
}

interface BehavioralActivationProps {
  userId: string;
  onBack: () => void;
  onComplete?: () => void;
}

type ExerciseStep = 'intro' | 'symptoms-before' | 'activity' | 'symptoms-after' | 'complete';

export const BehavioralActivation = ({ userId, onBack, onComplete }: BehavioralActivationProps) => {
  const [exerciseStep, setExerciseStep] = useState<ExerciseStep>('intro');
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [newActivity, setNewActivity] = useState('');
  const [pleasure, setPleasure] = useState([5]);
  const [mastery, setMastery] = useState([5]);
  const [symptomsBefore, setSymptomsBefore] = useState<Record<string, number>>({});
  const [symptomsAfter, setSymptomsAfter] = useState<Record<string, number>>({});
  const { logExercise, isLogging } = useUniversalTracking();

  const addActivity = () => {
    if (!newActivity.trim()) {
      toast.error('Please describe the activity');
      return;
    }

    const newLog: ActivityLog = {
      id: crypto.randomUUID(),
      date: format(new Date(), 'PPp'),
      activity: newActivity,
      pleasure: pleasure[0],
      mastery: mastery[0]
    };

    setLogs([newLog, ...logs]);
    setNewActivity('');
    setPleasure([5]);
    setMastery([5]);
    toast.success('Activity logged!');
  };

  const handleComplete = async () => {
    const success = await logExercise({
      exercise_type: 'behavioral_activation',
      exercise_name: 'Behavioural Activation',
      category: 'depression',
      duration_minutes: 10,
      completion_data: {
        activities_logged: logs.length,
        latest_activity: logs[0]?.activity,
        average_pleasure: logs.length > 0 ? logs.reduce((sum, l) => sum + l.pleasure, 0) / logs.length : 0,
        average_mastery: logs.length > 0 ? logs.reduce((sum, l) => sum + l.mastery, 0) / logs.length : 0
      },
      symptoms_before: symptomsBefore,
      symptoms_after: symptomsAfter,
    });

    if (success) {
      setExerciseStep('complete');
    }
  };

  const getColorForRating = (rating: number) => {
    if (rating >= 7) return 'bg-green-500/20 text-green-700 dark:text-green-400';
    if (rating >= 4) return 'bg-yellow-500/20 text-yellow-700 dark:text-yellow-400';
    return 'bg-red-500/20 text-red-700 dark:text-red-400';
  };

  if (exerciseStep === 'intro') {
    return (
      <div className="space-y-6 animate-fade-in">
        <Button variant="outline" onClick={onBack}>← Back to Depression Tools</Button>
        <Card className="bg-gradient-therapeutic border-primary/20">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-full bg-primary/10">
                <Activity className="h-7 w-7 text-primary" />
              </div>
              <div>
                <CardTitle className="text-2xl">Behavioural Activation</CardTitle>
                <p className="text-base text-foreground/80 mt-1">
                  Track pleasure and mastery from daily activities
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">
              Behavioural activation helps break the cycle of depression by gradually increasing 
              engagement in rewarding activities.
            </p>
            <Button onClick={() => setExerciseStep('symptoms-before')} className="w-full gap-2">
              Begin Session <ArrowRight className="w-4 h-4" />
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (exerciseStep === 'symptoms-before') {
    return (
      <div className="space-y-6 animate-fade-in">
        <Button variant="outline" onClick={() => setExerciseStep('intro')}>← Back</Button>
        <UniversalSymptomTracker mode="before" onRatingsChange={setSymptomsBefore} />
        <Button 
          onClick={() => setExerciseStep('activity')} 
          className="w-full"
          disabled={Object.keys(symptomsBefore).length === 0}
        >
          Continue to Activity Logging
        </Button>
      </div>
    );
  }

  if (exerciseStep === 'symptoms-after') {
    return (
      <div className="space-y-6 animate-fade-in">
        <UniversalSymptomTracker mode="after" previousRatings={symptomsBefore} onRatingsChange={setSymptomsAfter} />
        <Button 
          onClick={handleComplete} 
          className="w-full"
          disabled={Object.keys(symptomsAfter).length === 0 || isLogging}
        >
          {isLogging ? 'Saving...' : 'Complete Session'}
        </Button>
      </div>
    );
  }

  if (exerciseStep === 'complete') {
    return (
      <div className="space-y-6 animate-fade-in">
        <Card className="bg-gradient-to-br from-accent/20 to-primary/10 border-accent/30">
          <CardContent className="pt-6 text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-accent/20 flex items-center justify-center mx-auto">
              <Sparkles className="w-8 h-8 text-accent" />
            </div>
            <h3 className="text-xl font-semibold">Session Complete!</h3>
            <p className="text-muted-foreground">
              Keep tracking your activities over time to find patterns in what brings you pleasure and mastery.
            </p>
            <Button onClick={onComplete || onBack} className="w-full">Finish</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <Button variant="outline" onClick={() => setExerciseStep('symptoms-before')}>← Back</Button>

      <Card className="bg-gradient-therapeutic border-primary/20">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-full bg-primary/10">
              <Activity className="h-7 w-7 text-primary" />
            </div>
            <CardTitle className="text-2xl">Behavioural Activation Log</CardTitle>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          <Card className="bg-gradient-calm border-border/50">
            <CardContent className="pt-6 space-y-4">
              <div className="space-y-2">
                <label className="text-base font-medium">What did you do?</label>
                <Input
                  value={newActivity}
                  onChange={(e) => setNewActivity(e.target.value)}
                  placeholder="E.g., 'Walked for 10 minutes', 'Called a friend'"
                />
              </div>

              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <label className="text-base font-medium">Pleasure (P)</label>
                  <Badge className={getColorForRating(pleasure[0])}>{pleasure[0]}/10</Badge>
                </div>
                <Slider value={pleasure} onValueChange={setPleasure} max={10} step={1} />
              </div>

              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <label className="text-base font-medium">Mastery (M)</label>
                  <Badge className={getColorForRating(mastery[0])}>{mastery[0]}/10</Badge>
                </div>
                <Slider value={mastery} onValueChange={setMastery} max={10} step={1} />
              </div>

              <Button onClick={addActivity} className="w-full">
                <Plus className="mr-2 h-5 w-5" /> Log Activity
              </Button>
            </CardContent>
          </Card>

          {logs.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-lg font-medium">Recent Activities</h3>
              {logs.slice(0, 5).map((log) => (
                <Card key={log.id} className="border-border/50">
                  <CardContent className="pt-4">
                    <p className="font-medium">{log.activity}</p>
                    <p className="text-sm text-muted-foreground">{log.date}</p>
                    <div className="flex gap-2 mt-2">
                      <Badge className={getColorForRating(log.pleasure)}>P: {log.pleasure}/10</Badge>
                      <Badge className={getColorForRating(log.mastery)}>M: {log.mastery}/10</Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          <Button onClick={() => setExerciseStep('symptoms-after')} className="w-full">
            Finish & Rate How You Feel
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};
